import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Board from './Board';
import LEVELS from './levels';
import TEST_LEVELS from './test_levels';
import { sendTelemetry } from '../../utils/telemetry';

/**
 * MfersBeheadedGame - Main game container component
 *
 * Auth is handled internally via useLegacyAuth hook in Board.tsx
 */
function MfersBeheadedGame() {
  const { levelId } = useParams();
  const location = useLocation();
  const [boardKey, setBoardKey] = useState(Date.now());

  // Hide header on gameplay pages and fix PWA safe area
  useEffect(() => {
    document.body.classList.add('gameplay-page');

    // Fix PWA safe area: make html cover full screen including safe areas
    // Only apply explicit height in PWA mode to avoid scroll issues in regular browser
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  window.navigator.standalone === true;
    const originalHtmlHeight = document.documentElement.style.height;
    const originalHtmlMinHeight = document.documentElement.style.minHeight;
    const originalHtmlBg = document.documentElement.style.backgroundColor;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    if (isPWA) {
      const fullScreenHeight = Math.max(window.outerHeight, window.screen.height, window.innerHeight);
      document.documentElement.style.height = `${fullScreenHeight}px`;
      document.documentElement.style.minHeight = `${fullScreenHeight}px`;
    }
    // Always set overflow hidden on html to prevent iOS Safari rubber-banding
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.backgroundColor = '#000';

    // Disable pinch-to-zoom on game pages
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const originalViewport = viewportMeta?.getAttribute('content') || '';
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
    }

    // iOS ignores user-scalable=no, so we need to prevent gesture events
    const preventZoom = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };
    const preventGestureZoom = (e) => {
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventZoom, { passive: false });
    document.addEventListener('gesturestart', preventGestureZoom);
    document.addEventListener('gesturechange', preventGestureZoom);
    document.addEventListener('gestureend', preventGestureZoom);

    // Telemetry is sent from Board.tsx after name modal closes
    return () => {
      document.body.classList.remove('gameplay-page');

      // Restore original html styles
      document.documentElement.style.height = originalHtmlHeight;
      document.documentElement.style.minHeight = originalHtmlMinHeight;
      document.documentElement.style.backgroundColor = originalHtmlBg;
      document.documentElement.style.overflow = originalHtmlOverflow;

      // Restore original viewport
      if (viewportMeta && originalViewport) {
        viewportMeta.setAttribute('content', originalViewport);
      }

      // Remove zoom prevention listeners
      document.removeEventListener('touchmove', preventZoom);
      document.removeEventListener('gesturestart', preventGestureZoom);
      document.removeEventListener('gesturechange', preventGestureZoom);
      document.removeEventListener('gestureend', preventGestureZoom);
    };
  }, []);

  const reloadBoardKey = () => {
    setBoardKey(Date.now());
  };

  // Get the level configuration based on levelId or path
  const getLevelConfig = () => {
    // Handle special routes
    if (location.pathname.includes('pixelcrush')) {
      return LEVELS.LEVEL_5;
    }
    if (location.pathname.includes('tinydinos')) {
      return LEVELS.LEVEL_7;
    }

    // Handle test levels (localhost only)
    const isLocalhost = window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '';

    if (isLocalhost && levelId === 'autoplay_stress_test_level_013') {
      return TEST_LEVELS.AUTOPLAY_STRESS_TEST_LEVEL_013;
    }

    // Handle numbered levels
    switch (levelId) {
      case '1':
        return LEVELS.LEVEL_1;
      case '2':
        return LEVELS.LEVEL_2;
      case '4':
        return LEVELS.LEVEL_4;
      case '5':
        return LEVELS.LEVEL_5;
      case '7':
        return LEVELS.LEVEL_7;
      default:
        return LEVELS.LEVEL_1;
    }
  };

  const levelConfig = getLevelConfig();

  return (
    <div className="mfersbeheaded-game">
      <Board
        config={levelConfig}
        key={boardKey}
        reloadBoardKey={reloadBoardKey}
      />
    </div>
  );
}

export default MfersBeheadedGame;